// src/components/common/SEO.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

/** 사이트 정식 도메인 (www 없는 형태로 통일 — 중복 색인 방지) */
export const SITE_URL = 'https://www.goveinfo.link';
export const SITE_NAME = '고브인포';
export const SITE_TAGLINE = '정부지원금 정보센터';
export const DEFAULT_OG = `${SITE_URL}/og-image.png`;

interface SEOProps {
    /** 페이지 고유 제목 (사이트명은 자동으로 붙습니다) */
    title: string;
    description: string;
    keywords?: string;
    /** 사이트 루트 기준 경로 (예: '/housing') */
    path: string;
    image?: string;
    /** 콘텐츠 최종 수정일 YYYY-MM-DD */
    updated?: string;
    /** 검색결과 노출 제외 */
    noindex?: boolean;
    /** 추가 구조화 데이터 */
    jsonLd?: object | object[];
}

const SEO: React.FC<SEOProps> = ({
    title,
    description,
    keywords,
    path,
    image = DEFAULT_OG,
    updated,
    noindex = false,
    jsonLd,
}) => {
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;
    const fullTitle = path === '/' ? title : `${title} | ${SITE_NAME}`;
    const graphs = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

    return (
        <Helmet prioritizeSeoTags>
            <html lang="ko" />
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />
            {noindex ? (
                <meta name="robots" content="noindex, follow" />
            ) : (
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
            )}

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={`${SITE_NAME} ${SITE_TAGLINE}`} />
            <meta property="og:locale" content="ko_KR" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {updated && <meta property="article:modified_time" content={`${updated}T09:00:00+09:00`} />}

            {graphs.map((g, i) => (
                <script type="application/ld+json" key={i}>
                    {JSON.stringify(g)}
                </script>
            ))}
        </Helmet>
    );
};

/* ─────────────────────── 구조화 데이터 헬퍼 ─────────────────────── */

export const breadcrumbLd = (items: { name: string; path: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: it.name,
        item: `${SITE_URL}${it.path}`,
    })),
});

export const faqLd = (faq: { q: string; a: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
});

export const itemListLd = (
    name: string,
    items: { title: string; path: string }[]
) => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: it.title,
        url: `${SITE_URL}${it.path}`,
    })),
});

/** 개별 지원금 = 정부 서비스 */
export const govServiceLd = (p: {
    title: string;
    description: string;
    path: string;
    agency: string;
    amount: string;
    eligibility: string;
    updated: string;
}) => ({
    '@context': 'https://schema.org',
    '@type': 'GovernmentService',
    name: p.title,
    description: p.description,
    url: `${SITE_URL}${p.path}`,
    serviceType: '정부지원금',
    provider: { '@type': 'GovernmentOrganization', name: p.agency },
    areaServed: { '@type': 'Country', name: '대한민국' },
    audience: { '@type': 'Audience', audienceType: p.eligibility },
    availableChannel: {
        '@type': 'ServiceChannel',
        serviceUrl: `${SITE_URL}${p.path}`,
        availableLanguage: { '@type': 'Language', name: 'Korean', alternateName: 'ko' },
    },
    dateModified: p.updated,
});

/** 상세 페이지를 기사로도 인식시켜 색인 신호 강화 */
export const articleLd = (p: {
    title: string;
    description: string;
    path: string;
    updated: string;
}) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: p.title,
    description: p.description,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${p.path}` },
    datePublished: `${p.updated}T09:00:00+09:00`,
    dateModified: `${p.updated}T09:00:00+09:00`,
    inLanguage: 'ko-KR',
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png` },
    },
});

export default SEO;
